"use client";
import React, {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import { useDebounce } from "@onair/minimal-ui/hooks/use-debounce";
import { Scrollbar } from "@onair/minimal-ui/scrollbar";
import { toast } from "@onair/minimal-ui/snackbar";

import {
	useDeleteLibraryMutation,
	useDeleteResourceMutation,
	useGetLessonResourceByResourceIdQuery,
	useGetLibraryAndResourceByLibraryIdQuery,
	useLibraryCheckUsageInCoursesQuery,
	useResourceSearchLibraryAndResourceByKeywordMutation,
} from "@onair/operations";
import { GetResourceResponseDto, OrderByEnum } from "@onair/repositories";

import UploadFile from "./upload-file";
import CreateAndUpdateSubLibrary from "./create-and-update-sub-library";
import DocumentBreadcrumbs from "./document-breadcrumbs";
import DocumentItemList, { DocumentItemListProps } from "./document-item-list";
import DocumentSearch from "./document-search";
import ResourceDeleteDialog from "./resource-delete-dialog";
import { Iconify } from "@onair/minimal-ui/iconify";
import { MenuItem, Popover, Typography } from "@mui/material";
import { InterTypography } from "@onair/ui/typography";
import DocumentSort from "./document-sort";

const ROOT_LIBRARY_NAME = "Thư mục gốc";

interface IDocumentTabProps {
	rootLibraryId: string;
	selectResourceId: string | null;
	setSelectResourceId: (id: string) => void;
	currentResourceId: string | null;
	setCurrentResourceId: Dispatch<SetStateAction<string | null>>;
	setProgress: Dispatch<SetStateAction<number>>;
	setTotalUploadFile: Dispatch<SetStateAction<number>>;
	setTotalCompleteUploadFile: Dispatch<SetStateAction<number>>;
	setCurrentFileUpload: Dispatch<SetStateAction<string | null>>;
	isMultiple: boolean;
	multipleResources: string[];
	setMultipleResources: any;
	onSelectResourceItem: DocumentItemListProps["onSelectResource"];
}

const DocumentTab = (props: IDocumentTabProps) => {
	const {
		rootLibraryId,
		selectResourceId,
		currentResourceId,
		setCurrentResourceId,
		setProgress,
		setTotalUploadFile,
		setTotalCompleteUploadFile,
		setCurrentFileUpload,
		onSelectResourceItem,
		isMultiple,
		multipleResources,
		setMultipleResources,
	} = props;

	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [selectedLibraryId, setSelectedLibraryId] =
		useState<string>(rootLibraryId);
	const [selectedLibraryName, setSelectedLibraryName] =
		useState<string>(ROOT_LIBRARY_NAME);

	const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
	const [resourceToDelete, setResourceToDelete] = useState<string | null>(null);
	const [resourceFileName, setResourceFileName] = useState<string>("");
	const [libraryToDelete, setLibraryToDelete] = useState<string | null>(null);
	const [libraryFileName, setLibraryFileName] = useState<string>("");
	const [deleteLibraryDialogOpen, setDeleteLibraryDialogOpen] =
		useState<boolean>(false);
	const [subLibrary, setSubLibrary] = useState<{
		id: string | null;
		name: string | null;
	}>({
		id: null,
		name: null,
	});
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [showClearIcon, setShowClearIcon] = useState<boolean>(false);
	const [orderField, setOrderField] = useState<string>("createdAt");
	const [order, setOrder] = useState<OrderByEnum>(OrderByEnum.DESC);

	const debouncedQuery = useDebounce(searchQuery, 1000);

	const {
		data,
		isLoading,
		refetch: refetchGetLibraryAndResource,
	} = useGetLibraryAndResourceByLibraryIdQuery(
		selectedLibraryId,
		{},
		{ orderField, order },
	);

	const { data: searchData, mutateAsync: searchLibraryAndResource } =
		useResourceSearchLibraryAndResourceByKeywordMutation();

	const { mutateAsync: deleteLibrary } = useDeleteLibraryMutation({
		onSuccess: () => refetchGetLibraryAndResource(),
		onError: async () => {
			toast.error("Có lỗi xảy ra. Vui lòng thử lại sau");
		},
	});

	const [storageResources, setStorageResources] = useState<
		Record<string, (GetResourceResponseDto & { isNew: boolean })[]>
	>({});
	const [mapLoadedLibrary, setMapLoadedLibrary] = useState<
		Record<string, boolean>
	>({});

	useEffect(() => {
		if (isLoading) return;

		setMapLoadedLibrary((prev) => ({
			...prev,
			[selectedLibraryId]: true,
		}));

		const newResources = data?.resources || [];

		setStorageResources((prev) => {
			const prevList = prev[selectedLibraryId] || [];
			const isFirstLoad = prevList.length === 0;

			const updatedList = newResources.map((resource) => {
				const existing = prevList.find((r) => r.id === resource.id);
				if (existing) {
					return {
						...existing,
						...resource,
						isNew: false,
					};
				}
				return {
					...resource,
					isNew: isFirstLoad ? false : true,
				};
			});

			return {
				...prev,
				[selectedLibraryId]: updatedList,
			};
		});
	}, [data?.resources, isLoading, selectedLibraryId]);

	const { mutateAsync: deleteResource } = useDeleteResourceMutation({
		onSuccess: async () => refetchGetLibraryAndResource(),
		onError: async () => toast.error("Có lỗi xảy ra. Vui lòng thử lại sau"),
	});

	const { data: lessonResourceData } = useGetLessonResourceByResourceIdQuery(
		currentResourceId || "",
	);

	const { data: libraryUsageData, isLoading: isLibraryUsageLoading } =
		useLibraryCheckUsageInCoursesQuery(libraryToDelete || "");

	const handleDeleteResource = useCallback(
		(id: string) => {
			const resource = storageResources[selectedLibraryId]?.find(
				(r) => r.id === id,
			);
			if (resource) {
				setResourceFileName(resource.fileName);
			}

			setResourceToDelete(id);
			setDeleteDialogOpen(true);
		},
		[selectedLibraryId, storageResources],
	);

	const confirmDeleteResource = useCallback(() => {
		if (!resourceToDelete) return;

		deleteResource(resourceToDelete)
			.then(() => {
				toast.success("Xoá tài liệu thành công");
				setCurrentResourceId(null);
				setResourceToDelete(null);
				setDeleteDialogOpen(false);
			})
			.catch(() => {
				toast.error("Có lỗi xảy ra. Vui lòng thử lại sau");
			});
	}, [deleteResource, resourceToDelete]);

	useEffect(() => {
		if (debouncedQuery) {
			searchLibraryAndResource(debouncedQuery).then();
		} else {
			refetchGetLibraryAndResource().then();
		}
	}, [debouncedQuery]);

	const handleBreadcrumbClick = useCallback(() => {
		setSelectedLibraryId(rootLibraryId);
		setSelectedLibraryName(ROOT_LIBRARY_NAME);
	}, []);

	const handleSelectLibrary = useCallback((id: string, name = "") => {
		setSelectedLibraryId(id);
		setSelectedLibraryName(name);
	}, []);

	const handleOpenDialog = () => {
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setSearchQuery(value);
		setShowClearIcon(!!value.length);
	};

	const handleClearSearchQuery = async () => {
		setSearchQuery("");
		setShowClearIcon(false);

		await refetchGetLibraryAndResource();
	};

	const handleEditSubLibrary = (id: string, name: string) => {
		setSubLibrary({ id, name });
		handleOpenDialog();
	};

	const handleResetEditSubLibrary = () => {
		setSubLibrary({ id: null, name: null });
	};

	const handleDeleteLibrary = useCallback(
		(id: string) => {
			const library = data?.libraries?.find((lib) => lib.id === id);
			if (library) {
				setLibraryFileName(library.name);
			}

			setLibraryToDelete(id);
			setDeleteLibraryDialogOpen(true);
		},
		[data?.libraries],
	);

	const confirmDeleteLibrary = useCallback(() => {
		if (!libraryToDelete) return;

		deleteLibrary(libraryToDelete)
			.then(() => {
				toast.success("Xoá thư mục thành công");
				setLibraryToDelete(null);
				setDeleteLibraryDialogOpen(false);
			})
			.catch(() => {
				toast.error("Có lỗi xảy ra. Vui lòng thử lại sau");
			});
	}, [deleteLibrary, libraryToDelete]);

	const handleSortDocuments = (orderField: string, order: OrderByEnum) => {
		setOrderField(orderField as "fileName" | "name");
		setOrder(order);
	};

	return (
		<Box>
			<CreateAndUpdateSubLibrary
				id={selectedLibraryId}
				subLibrary={subLibrary}
				resetEditLibrary={handleResetEditSubLibrary}
				open={openDialog}
				onClose={handleCloseDialog}
				refetch={refetchGetLibraryAndResource}
			/>

			<Stack
				spacing={{ sm: 2, xs: 3 }}
				direction={{ sm: "row", xs: "column" }}
				alignItems="center"
				justifyContent="space-between"
			>
				<DocumentBreadcrumbs
					isRootLibrary={selectedLibraryId === rootLibraryId}
					onRootClick={handleBreadcrumbClick}
					selectedLibraryName={selectedLibraryName}
				/>

				<Stack direction={{ sm: "row", xs: "column" }} spacing={1} width={1}>
					<DocumentSearch
						searchQuery={searchQuery}
						showClearIcon={showClearIcon}
						onSearchChange={handleSearchChange}
						onClearSearch={handleClearSearchQuery}
					/>

					<Stack
						direction="row"
						flex={1}
						spacing={1}
						className="whitespace-nowrap"
					>
						<Button
							variant="outlined"
							onClick={handleOpenDialog}
							className="flex-1 text-nowrap !whitespace-nowrap"
							size="medium"
							sx={{
								whiteSpace: "nowrap",
							}}
						>
							Tạo thư mục
						</Button>

						<Box flex={1}>
							<UploadFile
								libraryId={selectedLibraryId}
								refetch={refetchGetLibraryAndResource}
								setCurrentFileUpload={setCurrentFileUpload}
								setProgress={setProgress}
								setTotalUploadFile={setTotalUploadFile}
								setTotalCompleteUploadFile={setTotalCompleteUploadFile}
							/>
						</Box>
					</Stack>
				</Stack>
			</Stack>

			<DocumentSort
				handleSortDocuments={handleSortDocuments}
				orderField={orderField}
			/>

			<Box className="mt-4">
				<Scrollbar fillContent>
					<Grid container spacing={2} columns={24}>
						{debouncedQuery && searchData ? (
							<DocumentItemList
								isLoading={isLoading}
								libraries={searchData.libraries}
								resources={searchData.resources}
								selectedResourceId={selectResourceId}
								currentResourceId={currentResourceId}
								setCurrentResourceId={setCurrentResourceId}
								onSelectLibrary={handleSelectLibrary}
								onEditSubLibrary={handleEditSubLibrary}
								onDeleteLibrary={handleDeleteLibrary}
								onDeleteResource={handleDeleteResource}
								isMultiple={isMultiple}
								multipleResources={multipleResources}
								setMultipleResources={setMultipleResources}
								setSelectResourceId={props.setSelectResourceId}
								refetchLibraryAndResource={refetchGetLibraryAndResource}
								onSelectResource={onSelectResourceItem}
							/>
						) : (
							<DocumentItemList
								isLoading={isLoading}
								libraries={data?.libraries || []}
								resources={storageResources?.[selectedLibraryId] || []}
								selectedResourceId={selectResourceId}
								currentResourceId={currentResourceId}
								setCurrentResourceId={setCurrentResourceId}
								onSelectLibrary={handleSelectLibrary}
								onEditSubLibrary={handleEditSubLibrary}
								onDeleteLibrary={handleDeleteLibrary}
								onDeleteResource={handleDeleteResource}
								isMultiple={isMultiple}
								multipleResources={multipleResources}
								setMultipleResources={setMultipleResources}
								setSelectResourceId={props.setSelectResourceId}
								refetchLibraryAndResource={refetchGetLibraryAndResource}
								onSelectResource={onSelectResourceItem}
							/>
						)}
					</Grid>
				</Scrollbar>
			</Box>

			<ResourceDeleteDialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
				onConfirm={confirmDeleteResource}
				fileName={resourceFileName}
				courseData={lessonResourceData}
			/>

			<ResourceDeleteDialog
				open={deleteLibraryDialogOpen}
				onClose={() => setDeleteLibraryDialogOpen(false)}
				onConfirm={confirmDeleteLibrary}
				fileName={libraryFileName}
				courseData={libraryUsageData?.courses}
				isLoading={isLibraryUsageLoading}
			/>
		</Box>
	);
};

export default DocumentTab;
